// Musook role hierarchy (mirrors backend)
export const ROLE_LEVEL = {
    owner: 100,
    manager: 90,
    booking_manager: 80,
    front_desk: 60,
    producer: 55,
    mixing_engineer: 55,
    mastering_engineer: 55,
    engineer: 50,
    accountant: 50,
    client: 10,
};

export const ROLE_LABEL = {
    owner: "Studio Owner",
    manager: "General Manager",
    booking_manager: "Booking Manager",
    front_desk: "Front Desk",
    engineer: "Engineer",
    producer: "Producer",
    mixing_engineer: "Mixing Engineer",
    mastering_engineer: "Mastering Engineer",
    accountant: "Accountant",
    client: "Client",
};

export function canSee(userOrRole, section) {
    const user = typeof userOrRole === "object" ? userOrRole : null;
    const role = user?.role || userOrRole;
    const effective = new Set(user?.authorization?.permissions || []);
    const permissionRules = {
        dashboard: [],
        calendar: ["booking.view"],
        availability: ["location.view", "team.view"],
        bookings: ["booking.view"],
        crm: ["client.view"],
        clients: ["client.view"],
        projects: ["project.view"],
        invoices: ["finance.view_revenue", "payment.collect"],
        automations: ["organization.manage_settings"],
        analytics: ["finance.view_revenue"],
        admin: ["organization.manage_settings", "location.update"],
        team: ["team.manage_members", "team.view"],
        inbox: [],
        portal: [],
        book: [],
    };
    const required = permissionRules[section];
    if (user?.authorization) {
        if (section === "portal") return role === "client";
        return required.length === 0 || required.some((permission) => effective.has(permission));
    }
    const level = ROLE_LEVEL[role] ?? 0;
    const rules = {
        dashboard: 40, calendar: 40, availability: 40, bookings: 40, crm: 50,
        clients: 40, projects: 40, invoices: 50, automations: 80, analytics: 80,
        admin: 80, team: 80, inbox: 10, portal: 10, book: 0,
    };
    return level >= (rules[section] ?? 50);
}

export const STATUS_PILL = {
    // bookings
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    confirmed: { label: "Confirmed", cls: "bg-teal-100 text-teal-800 border border-teal-200" },
    in_progress: { label: "In session", cls: "bg-blue-100 text-blue-800 border border-blue-200" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    cancelled: { label: "Cancelled", cls: "bg-neutral-200 text-neutral-700 border border-neutral-300" },
    no_show: { label: "No-show", cls: "bg-red-100 text-red-800 border border-red-200" },
    // invoices
    draft: { label: "Draft", cls: "bg-neutral-100 text-neutral-700 border border-neutral-200" },
    deposit_pending: { label: "Deposit due", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    partially_paid: { label: "Partially paid", cls: "bg-blue-100 text-blue-800 border border-blue-200" },
    paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    refunded: { label: "Refunded", cls: "bg-neutral-100 text-neutral-700 border border-neutral-200" },
    // CRM
    new_inquiry: { label: "New inquiry", cls: "bg-sky-100 text-sky-800 border border-sky-200" },
    qualification_needed: { label: "Qualifying", cls: "bg-sky-100 text-sky-800 border border-sky-200" },
    quote_sent: { label: "Quote sent", cls: "bg-indigo-100 text-indigo-800 border border-indigo-200" },
    awaiting_response: { label: "Awaiting", cls: "bg-yellow-100 text-yellow-800 border border-yellow-200" },
    active: { label: "Active", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    project_in_progress: { label: "In production", cls: "bg-teal-100 text-teal-800 border border-teal-200" },
    follow_up_required: { label: "Follow up", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    repeat_opportunity: { label: "Repeat lead", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    inactive: { label: "Inactive", cls: "bg-neutral-100 text-neutral-600 border border-neutral-200" },
    lost_lead: { label: "Lost", cls: "bg-neutral-200 text-neutral-700 border border-neutral-300" },
    delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    // projects
    intake: { label: "Intake", cls: "bg-sky-100 text-sky-800 border border-sky-200" },
    pre_production: { label: "Pre-production", cls: "bg-sky-100 text-sky-800 border border-sky-200" },
    recording: { label: "Recording", cls: "bg-teal-100 text-teal-800 border border-teal-200" },
    editing: { label: "Editing", cls: "bg-indigo-100 text-indigo-800 border border-indigo-200" },
    mixing: { label: "Mixing", cls: "bg-purple-100 text-purple-800 border border-purple-200" },
    mastering: { label: "Mastering", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    review: { label: "Client review", cls: "bg-blue-100 text-blue-800 border border-blue-200" },
    revisions: { label: "Revisions", cls: "bg-orange-100 text-orange-800 border border-orange-200" },
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    archived: { label: "Archived", cls: "bg-neutral-100 text-neutral-600 border border-neutral-200" },
};

export function pill(status) {
    return STATUS_PILL[status] || { label: status, cls: "bg-neutral-100 text-neutral-700" };
}

export const CRM_STAGES = [
    "new_inquiry",
    "quote_sent",
    "awaiting_response",
    "deposit_pending",
    "confirmed",
    "active",
    "project_in_progress",
    "delivered",
    "repeat_opportunity",
    "follow_up_required",
    "inactive",
];

export const PROJECT_STAGES = [
    "intake",
    "pre_production",
    "recording",
    "editing",
    "mixing",
    "mastering",
    "review",
    "revisions",
    "approved",
    "delivered",
];
